import React, { useEffect, useState } from 'react';
import { Layers, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const TONE = { accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200', declined: 'bg-red-50 text-red-700 border-red-200',
  superseded: 'bg-slate-100 text-slate-500 border-slate-200', sent: 'bg-sky-50 text-sky-700 border-sky-200', draft: 'bg-amber-50 text-amber-700 border-amber-200' };
const money = (v) => `$${(v || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Job page: the quotes that belong to this job, with status, value and option ranges.
export const JobQuotesPanel = ({ parentId }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/jobs/${parentId}/quotes`).then((r) => setData(r.data)).catch(() => setData(null)); }, [api, parentId]);

  if (!data) return null;
  if (!data.quotes.length) return null;
  return (
    <div data-testid="job-quotes-panel" className="bg-white border border-[#E2E2DF] rounded-md p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Layers size={15} className="text-[#0F4F1B]" />
          <div className="text-sm font-bold text-slate-800">Job · {data.quotes.length} quote{data.quotes.length === 1 ? '' : 's'}</div>
          <span className="text-[11px] text-slate-500">This appraisal is the job; its quotes carry the prices.</span>
        </div>
        <div className="flex gap-4 text-xs">
          <span data-testid="job-value-potential">Potential <b className="font-mono">{money(data.job_value_potential)}</b></span>
          <span data-testid="job-value-accepted">Won <b className="font-mono text-emerald-700">{money(data.job_value_accepted)}</b></span>
        </div>
      </div>
      <div className="divide-y divide-[#F1EFEC]">
        {data.quotes.map((q) => (
          <button key={q.id} data-testid={`job-quote-row-${q.appraisal_no}`} onClick={() => navigate(`/appraisals/${q.id}`)}
            className="w-full text-left grid grid-cols-[90px_1fr_110px_120px] gap-2 items-center py-2 hover:bg-slate-50 transition-colors px-1">
            <span className="font-mono text-xs text-slate-600">A{q.appraisal_no}</span>
            <span className="text-xs text-slate-800 truncate">
              {q.quotation_type || q.category}{q.variant_label ? ` · ${q.variant_label}` : ''}
              {q.option_group && <span className="ml-2 text-[10px] text-slate-400">{q.status === 'accepted' ? 'chosen option' : q.status === 'superseded' ? 'not chosen' : q.is_primary ? 'primary option' : 'alternative'}</span>}
            </span>
            <span className={`text-[10px] font-semibold uppercase tracking-wider border rounded px-2 py-0.5 w-fit ${TONE[q.status] || TONE.draft}`}>{q.status}</span>
            <span className="font-mono text-xs text-right">{money(q.value)}</span>
          </button>
        ))}
      </div>
      {data.option_ranges.map((g) => (
        <div key={g.group} data-testid="job-option-range" className="mt-2 text-[11px] text-slate-500">
          {g.category} options {money(g.min)}–{money(g.max)}: {g.options.map((o) => `${o.label} ${money(o.value)}`).join(' · ')}
        </div>
      ))}
    </div>
  );
};

export const JobQuotesPanelLoader = ({ parentId }) => (parentId ? <JobQuotesPanel parentId={parentId} /> : <Loader2 className="animate-spin" />);
